// utils/issueMoneyImpact.js
//
// What ONE answer to a collection issue does to the member's LINE total, in
// cents. Pure, prop-shaped (works on a pinia-orm instance or a plain jsona
// payload), and deliberately conservative: every helper returns `null` rather
// than a figure it cannot stand behind, so a caller can only ever render a
// number that is actually derivable.
//
// Three rules carried forward unchanged through the 2026-08-10 redesign:
//   • Never state a figure the member cannot cause.
//   • A figure that cannot be known is OMITTED — never rendered as zero.
//   • The figure sits on the control that causes it, never in a summary above.
//
// ── Why this is line-level only ──────────────────────────────────────────────
// The projected BOX total and any wallet effect are NOT derivable client-side.
// Nothing here may be summed into a box figure.
//
// ── What the backend actually does ───────────────────────────────────────────
// Read from `../backend/app/Services/Commerce/ItemIssueService.php`, not assumed.
// The per-reason taxonomy is gone; there are now two money-bearing answers.
//
//  • YES + an option → `collect()`. The option drives the line, in this order:
//    a `qty` shrinks the line first through
//    `CollectionPlanService::leaderEditPlanItem(reflect_to_order_box_item)`, so
//    the generated `amount` follows the quantity down — THIS is the shortfall
//    money fix, and it closes the 2026-08-09 FE→BE brief: the member is now
//    billed for what arrives. A `price_cents` / `variant_id` / `supplier_id`
//    then goes through the substitution path at that price. An option that pins
//    nothing down (a typed "navy" with no catalogue row) collects as ordered.
//
//  • NO → `markOutOfStock()`. Its own docblock: "releases the derived
//    reservation — NOT a refund, no wallet call". OUT_OF_STOCK leaves the
//    billable set, so the line genuinely comes off. Fully derivable.
//
//  • WAIT → moves to the next plan. Nothing is charged and nothing is removed
//    from THIS line yet; there is no line figure to state, so no helper offers
//    one.
//
// ── The formula, and the discount that must not be dropped ───────────────────
// An OrderBoxItem's `amount` is a GENERATED column, `(qty * price) - discount`
// (`2025_11_16_133034_add_notes_to_order_box_items_table.php:18`), and neither
// the qty shrink nor the substitution path touches `discount`. So the line an
// option produces is:
//
//     (option.qty ?? item.qty) × (option.priceCents ?? item.price) − item.discount
//
// A naive `qty × unit price` overstates a Poola line by the WHOLE discount —
// the PoolShare-funded share lives in `discount`, which is a flat cents figure
// written once by `OrderBoxService::addPoolaFulfillmentItem()`, not a
// proportion, so it carries forward unchanged rather than being re-derived.

/**
 * The line's NET total in cents, or null when it genuinely cannot be known.
 *
 * Same precedence as {@link lineNetCents} in `orderBoxItemDisplay.js` (the
 * app-wide SSOT: backend-computed net `amount` first, gross `price * qty` only
 * as the pre-`amount` fallback) — but returns null instead of 0 for an
 * unknowable line, because "0.00 ج.م" is a lie and an omitted line is not.
 *
 * @param {Object|null|undefined} item order-box item (model instance or plain)
 * @returns {number|null} net line total in cents, or null
 */
export function knownLineNetCents(item) {
  if (!item) return null;
  if (Number.isFinite(item.amount)) return item.amount;
  if (Number.isFinite(item.price) && Number.isFinite(item.qty)) {
    return item.price * item.qty;
  }
  return null;
}

/**
 * The line impact of saying YES to ONE offered option.
 *
 * Three guards, each of which exists because getting it wrong states a figure
 * the backend will not charge:
 *
 *  1. **Subtract the discount.** See the module header. Worked example: an item
 *     of 4 × 120.00 with a 60.00 discount has `amount` 420.00; a 2-unit option
 *     is `2 × 120.00 − 60.00 = 180.00`, NOT 240.00.
 *  2. **State nothing on an already-collected item.** `collect()` returns at its
 *     COLLECTED branch BEFORE the option is applied — a price option changes
 *     nothing, and a qty option throws outright (partial return, which the
 *     returns system owns). Any figure would be one the backend will not charge.
 *  3. Refuse anything the backend itself refuses: a qty below 1 and a price of
 *     zero or less are both rejected by `normalizeOptions()` at raise time, so
 *     an option carrying one is malformed, not a bargain.
 *
 * An option that changes neither price nor qty legitimately returns a ZERO
 * delta — "this one costs you nothing extra" is a real, derivable answer, and
 * distinct from `null`, which means "we cannot know". The caller decides
 * whether a zero delta is worth printing.
 *
 * @param {Object} args
 * @param {Object|null} args.item order-box item (needs qty, price, discount)
 * @param {{ priceCents?: number|null, qty?: number|null }|null} args.option
 * @param {boolean} [args.isCollected] the goods are already in her box
 * @returns {{ currentCents: number, resultingCents: number, deltaCents: number }|null}
 */
export function optionLineImpact({ item, option, isCollected = false }) {
  // GUARD 2 — the answer cannot move this line, whatever the option says.
  if (isCollected) return null;
  if (!item || !option) return null;

  const currentCents = knownLineNetCents(item);
  if (!Number.isFinite(currentCents) || currentCents <= 0) return null;

  // ABSENT and MALFORMED are not the same thing, and collapsing them is a money
  // bug. Null/undefined means "this option leaves that axis alone" → fall back
  // to the item. Anything else present is a payload we do not understand, and
  // falling back there would silently reinterpret "2 units offered" as "the 4
  // she ordered" — overstating the line by exactly the shortfall.
  const qty = option.qty == null ? item.qty : option.qty;
  const priceCents = option.priceCents == null ? item.price : option.priceCents;

  // GUARD 3 — mirror the backend's own raise-time rules rather than projecting
  // a line it would have refused to offer: `normalizeOptions()` rejects a qty
  // below 1 and a price of zero or less at raise time.
  if (!Number.isFinite(qty) || qty < 1) return null;
  if (!Number.isFinite(priceCents) || priceCents <= 0) return null;

  // GUARD 1 — the discount is a flat cents figure the shrink never touches.
  // Required, not defaulted: `amount` is generated from it, so every real
  // payload carries it, and assuming 0 on a malformed one would silently
  // overstate a Poola line by the entire PoolShare-funded share.
  if (!Number.isFinite(item.discount)) return null;

  const resultingCents = qty * priceCents - item.discount;

  // A line that projects BELOW zero means the inputs disagree with each other
  // (a discount larger than the whole revised line). Refuse rather than clamp:
  // "-16.00" is nonsense, but so is a silently clamped "0.00" that hides it.
  if (resultingCents < 0) return null;

  return { currentCents, resultingCents, deltaCents: resultingCents - currentCents };
}

/**
 * The line impact of declining the item: the whole line comes off the order.
 *
 * Carries the SAME already-collected guard as `optionLineImpact`. NO is offered
 * on every question now — including questions about goods already in her box —
 * and there "the whole line comes off your order" is simply false: the item is
 * in the carton, `collect()`'s COLLECTED branch closes the issue without
 * touching money, and what actually happens next is an operational follow-up,
 * not a refund. A figure here would be the most misleading one on the card.
 *
 * @param {Object} args
 * @param {number|null} args.lineTotalCents current net line total
 * @param {boolean} [args.isCollected] the goods are already in her box
 * @returns {{ currentCents: number, resultingCents: number, deltaCents: number }|null}
 */
export function declineLineImpact({ lineTotalCents, isCollected = false }) {
  if (isCollected) return null;
  if (!Number.isFinite(lineTotalCents) || lineTotalCents <= 0) return null;

  return { currentCents: lineTotalCents, resultingCents: 0, deltaCents: -lineTotalCents };
}
